import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Modal,
  Input,
  DatePicker,
  Spin,
  Tabs,
  Table,
  Space,
  Tag,
  Typography,
  message
} from 'antd';
import { ArrowLeftOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { dashboardRoleKind, routeDefaultTabKey } from '../../utils/roleAwareEntityNavigation';
import { ApplicationCell, PageLayout, StatusCell } from '../../ui';
import type { Route } from '../../types';
import { RouteStatus } from '../../types';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { useCreateLayoutMutation } from '../../store/api/calculationsApi';
import {
  useGetRouteByIdQuery,
  useGetRouteViewQuery,
  useGetExecutionProgressQuery,
  useLoadRouteStopMutation,
  useUnloadRouteStopMutation
} from '../../store/api/routesApi';
import { buildStopExecutionIndex } from '../../features/operationalCockpit';
import { isOperationalCockpitMode } from '../../features/routes/execution/executionProjectionConsumerFlags';
import { normalizeRouteStatus } from '../../utils/statusUtils';
import RouteEventsModal from '../../components/routes/RouteEventsModal';
import PenaltiesBlock from '../../components/routes/routeDetails/PenaltiesBlock';
import RouteStructureBlock from '../../components/routes/routeDetails/RouteStructureBlock';
import CargoBlock from '../../components/routes/routeDetails/CargoBlock';
import RoutePrimaryInfoBlock from '../../components/routes/routeDetails/RoutePrimaryInfoBlock';
import RouteKpiSummaryBlock from '../../components/routes/routeDetails/RouteKpiSummaryBlock';
import RouteTasksTab from '../../components/routes/routeDetails/RouteTasksTab';
import RouteFinanceTab from '../../components/routes/routeDetails/RouteFinanceTab';
import RouteDocumentsTab from '../../components/routes/routeDetails/RouteDocumentsTab';
import { RouteExecutionTab } from '../../features/executionOperations';
import {
  RouteMutationWorkspaceTab,
  isLiveRouteStatus,
  isMutationWorkspaceEnabled
} from '../../features/mutationWorkspace';
import {
  RouteCorrectionWorkspaceTab,
  isRouteCorrectionWorkspaceEnabled
} from '../../features/routeCorrection';
import { navigateToReferenceCard } from '../../utils/referenceCardNavigation';
import { routeViewCargoAllocationRefreshToken } from '../../utils/routeViewCargoAllocationRefreshToken';
import {
  isLiveApplicationInjectionEnabled,
  LiveApplicationInjectionWizard
} from '../../features/liveApplicationInjection';

const { Text } = Typography;

const ROUTE_CARD_TAB_KEYS = new Set([
  'card',
  'execution',
  'correction',
  'mutation',
  'tasks',
  'finance',
  'documents',
  'cargo',
  'penalties',
  'applications',
]);

/**
 * Полноэкранная карточка маршрута (тот же контент, что в модальном окне списка, с вкладками).
 */
const RouteCardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleName = useSelector((s: RootState) => s.auth.user?.role?.name);
  const defaultRouteTab = useMemo(() => routeDefaultTabKey(dashboardRoleKind(roleName)), [roleName]);
  const tabParam = searchParams.get('tab');
  const stopIdParam = searchParams.get('stopId');
  const focusedStopId = useMemo(() => {
    if (!stopIdParam) return null;
    const parsed = parseInt(stopIdParam, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [stopIdParam]);
  const activeTabKey = useMemo(() => {
    if (tabParam && ROUTE_CARD_TAB_KEYS.has(tabParam)) return tabParam;
    if (focusedStopId != null) return 'card';
    return defaultRouteTab;
  }, [tabParam, defaultRouteTab, focusedStopId]);
  const { id: idParam } = useParams<{ id: string }>();
  const routeId = idParam ? parseInt(idParam, 10) : NaN;
  const skip = Number.isNaN(routeId);

  const { data: route, isLoading: routeLoading, isError: routeErr, error: routeError } = useGetRouteByIdQuery(
    routeId,
    { skip: skip }
  );

  const { data: view, isLoading: viewLoading, isError: viewIsErr, error: viewError, refetch } = useGetRouteViewQuery(
    routeId,
    { skip: skip || !route }
  );

  const { data: executionProgress } = useGetExecutionProgressQuery(routeId, {
    skip: skip || !isOperationalCockpitMode()
  });
  const stopExecutionIndex = useMemo(
    () => (isOperationalCockpitMode() ? buildStopExecutionIndex(executionProgress) : undefined),
    [executionProgress]
  );

  const [createLayout] = useCreateLayoutMutation();
  const [loadStop, { isLoading: loadingStop }] = useLoadRouteStopMutation();
  const [unloadStop, { isLoading: unloadingStop }] = useUnloadRouteStopMutation();

  const [isEventsModalVisible, setIsEventsModalVisible] = useState(false);
  const [reopenModalVisible, setReopenModalVisible] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenLoading, setReopenLoading] = useState(false);
  const [stopAction, setStopAction] = useState<{
    visible: boolean;
    type: 'load' | 'unload' | null;
    stopId?: number;
    date?: dayjs.Dayjs;
  }>({ visible: false, type: null });

  const [injectWizardOpen, setInjectWizardOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const injectionEnabled = isLiveApplicationInjectionEnabled();
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
    if (!token) return undefined;
    fetch(`${API_BASE_URL}/auth/profile`, {
      headers: { Authorization: token ? `Bearer ${token}` : '' }
    })
      .then((r) => r.json())
      .then((json) => {
        if (!mounted) return;
        const roleCandidate = json?.data?.role || json?.role || json?.data?.user?.role;
        const roleName = typeof roleCandidate === 'string' ? roleCandidate : roleCandidate?.name;
        setIsAdmin(roleName === 'admin');
      })
      .catch(() => {
        if (!mounted) return;
        setIsAdmin(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const refreshCard = () => {
    void refetch();
  };

  const handleReopen = async () => {
    if (!route) return;
    if (!reopenReason || reopenReason.trim().length < 5) {
      message.error('Укажите причину отката (не менее 5 символов)');
      return;
    }
    setReopenLoading(true);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
      const res = await fetch(`${API_BASE_URL}/routes/${route.id}/reopen`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ reason: reopenReason })
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json?.error || 'Ошибка при откате маршрута');
      }
      message.success('Маршрут открыт для редактирования');
      setReopenModalVisible(false);
      setReopenReason('');
      window.location.reload();
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : 'Ошибка при откате маршрута');
    } finally {
      setReopenLoading(false);
    }
  };

  const op = view?.operation;
  const statusRaw = op?.status ?? route?.status;
  const normalized = normalizeRouteStatus(statusRaw);
  const adminReopen =
    isAdmin && normalized === RouteStatus.COMPLETED ? (
      <Button danger size="small" onClick={() => setReopenModalVisible(true)}>
        Откат маршрута
      </Button>
    ) : null;

  const appRows = useMemo(() => {
    const summary = view?.operation?.linkedApplicationsSummary ?? [];
    return summary.map((a) => ({
      key: String(a.applicationId),
      application: {
        id: a.applicationId,
        internalNumber: a.internalNumber?.trim() ? a.internalNumber.trim() : String(a.applicationId),
        applicationDate: a.applicationDate ?? null
      }
    }));
  }, [view?.operation?.linkedApplicationsSummary]);

  if (skip) {
    return (
      <PageLayout title="Маршрут">
        <Alert type="error" message="Некорректная ссылка" showIcon />
      </PageLayout>
    );
  }

  if (routeLoading) {
    return (
      <PageLayout title="Маршрут" titleLevel={2}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (routeErr || !route) {
    const msg =
      (routeError as { data?: { error?: string } })?.data?.error ||
      'Не удалось загрузить маршрут. Возможно, запись удалена или недоступна.';
    return (
      <PageLayout title="Маршрут">
        <Alert type="error" message={msg} showIcon />
      </PageLayout>
    );
  }

  const r = route as Route;

  const toolbar = (
    <Space wrap style={{ marginBottom: 12 }}>
      <StatusCell status={String(statusRaw ?? '')} />
      {String(op?.executor ?? r.executor ?? '').toUpperCase() === 'PARTNER' && (
        <Tag color="blue">Оплата партнёром</Tag>
      )}
      <Button
        size="small"
        onClick={async () => {
          try {
            const draft = await createLayout({ routeId: r.id, inputPayload: { source: 'route' } }).unwrap();
            window.location.href = `/calculations/${(draft as { id: number }).id}`;
          } catch {
            /* empty */
          }
        }}
      >
        Расчёт размещения
      </Button>
      <Button size="small" icon={<HistoryOutlined />} onClick={() => setIsEventsModalVisible(true)}>
        История событий
      </Button>
      {injectionEnabled &&
      view &&
      normalized !== RouteStatus.COMPLETED &&
      normalized !== RouteStatus.CANCELLED ? (
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setInjectWizardOpen(true)}
        >
          Добавить заявку
        </Button>
      ) : null}
      {adminReopen}
    </Space>
  );

  const blocksCard =
    view ? (
      <>
        <RouteKpiSummaryBlock
          execution={view.execution}
          routePenalties={view.routePenalties}
          financeStatus={view.finance?.aggregatedStatus ?? null}
          documents={view.documents}
        />
        <RoutePrimaryInfoBlock
          model={view.operation}
          structure={view.structure}
          parties={view.parties}
          applications={view.applications}
          routeNumber={r.number ?? null}
        />
        <RouteStructureBlock
          model={view.structure}
          routeDirectionLabel={view.operation.direction.label}
          routeLoadingCity={view.operation.direction.loadingCity}
          routeUnloadingCity={view.operation.direction.unloadingCity}
          applications={view.applications}
          loadingWarehouse={view.operation.loadingWarehouse}
          unloadingWarehouse={view.operation.unloadingWarehouse}
          routeStatus={view.operation.status}
          loadingStop={loadingStop}
          unloadingStop={unloadingStop}
          showCargoSummary={false}
          stopExecutionIndex={stopExecutionIndex}
          focusedStopId={focusedStopId}
          onRequestLoad={(stopId) => setStopAction({ visible: true, type: 'load', stopId })}
          onRequestUnload={(stopId) => setStopAction({ visible: true, type: 'unload', stopId })}
        />
      </>
    ) : null;

  const showCorrectionTab =
    isRouteCorrectionWorkspaceEnabled() && view != null && isLiveRouteStatus(view.operation.status);

  const showMutationTab =
    isMutationWorkspaceEnabled() && view != null && isLiveRouteStatus(view.operation.status);

  const tabItems = [
    {
      key: 'card',
      label: 'Карточка',
      children: (
        <div className="entity-card-tab-body">
          {viewLoading && !view && (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          )}
          {viewIsErr && (
            <Alert
              type="error"
              showIcon
              message="Не удалось загрузить детали маршрута"
              description={viewError && 'data' in viewError ? String((viewError as { data?: unknown }).data) : undefined}
            />
          )}
          {toolbar}
          {blocksCard}
        </div>
      )
    },
    {
      key: 'execution',
      label: 'Исполнение',
      children: (
        <div className="entity-card-tab-body">
          {view ? (
            <RouteExecutionTab route={r} view={view} onSuccess={refreshCard} />
          ) : (
            <Spin />
          )}
        </div>
      )
    },
    ...(showCorrectionTab
      ? [
          {
            key: 'correction',
            label: 'Корректировка маршрута',
            children: (
              <div className="entity-card-tab-body">
                <RouteCorrectionWorkspaceTab
                  route={r}
                  routeId={r.id}
                  view={view!}
                  onSuccess={refreshCard}
                />
              </div>
            )
          }
        ]
      : []),
    ...(showMutationTab
      ? [
          {
            key: 'mutation',
            label: 'Изменение маршрута',
            children: (
              <div className="entity-card-tab-body">
                <RouteMutationWorkspaceTab routeId={r.id} view={view!} onSuccess={refreshCard} />
              </div>
            )
          }
        ]
      : []),
    {
      key: 'tasks',
      label: 'Задачи',
      children: (
        <div className="entity-card-tab-body">
          {view ? <RouteTasksTab route={r} view={view} onSuccess={refreshCard} /> : <Spin />}
        </div>
      )
    },
    {
      key: 'finance',
      label: 'Финансы',
      children: (
        <div className="entity-card-tab-body">
          {view ? (
            <RouteFinanceTab
              finance={view.finance ?? null}
              carrierFinance={view.carrierFinance ?? null}
              documents={view.documents}
              tasks={view.execution.tasks}
              isCompanyRoute={view.operation.isCompanyRoute}
              route={r}
            />
          ) : (
            <Spin />
          )}
        </div>
      )
    },
    {
      key: 'documents',
      label: 'Документы',
      children: (
        <div className="entity-card-tab-body">
          {view ? (
            <RouteDocumentsTab
              model={view.documents}
              route={r}
              applications={view.applications}
              onUploadSuccess={refreshCard}
            />
          ) : (
            <Spin />
          )}
        </div>
      )
    },
    {
      key: 'cargo',
      label: 'Грузы',
      children: (
        <div className="entity-card-tab-body">
          {view ? (
            <CargoBlock
              cargoLines={view.cargo}
              summary={view.structure.cargoSummary}
              allocations={view.structure.allocations}
              routeId={routeId}
              linkedApplicationIds={view.operation.linkedApplicationsSummary.map((s) => s.applicationId)}
              allocationRefreshToken={routeViewCargoAllocationRefreshToken(view)}
            />
          ) : (
            <Spin />
          )}
        </div>
      )
    },
    {
      key: 'penalties',
      label: 'Штрафы',
      children: (
        <div className="entity-card-tab-body">
          {view ? <PenaltiesBlock penalties={view.routePenalties} /> : <Spin />}
        </div>
      )
    },
    {
      key: 'applications',
      label: `Заявки (${appRows.length})`,
      children: (
        <div className="entity-card-tab-body">
          {appRows.length === 0 ? (
            <Text type="secondary">По маршруту нет привязанных заявок</Text>
          ) : (
            <Table
              size="small"
              rowKey="key"
              dataSource={appRows}
              pagination={false}
              className="entity-card-table table-card-rows"
              onRow={(rec) => ({
                onClick: () => navigateToReferenceCard(navigate, `/applications/${rec.application.id}`, location),
                style: { cursor: 'pointer' }
              })}
              columns={[
                {
                  title: 'Заявка',
                  key: 'num',
                  render: (_: unknown, rec: { application: { id: number; internalNumber: string; applicationDate: string | null } }) => (
                    <ApplicationCell application={rec.application} />
                  )
                },
                {
                  title: '',
                  key: 'o',
                  align: 'right',
                  render: (_: unknown, rec: { application: { id: number } }) => (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => navigateToReferenceCard(navigate, `/applications/${rec.application.id}`, location)}
                    >
                      Открыть
                    </Button>
                  )
                }
              ]}
            />
          )}
        </div>
      )
    }
  ];

  return (
    <PageLayout
      titleLevel={2}
      title={`Маршрут ${r.number || '#' + r.id}`}
      actions={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/routes')}>
            К списку маршрутов
          </Button>
        </Space>
      }
    >
      <Tabs
        activeKey={activeTabKey}
        onChange={(key) => {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', key);
              return next;
            },
            { replace: true }
          );
        }}
        className="entity-card-tabs"
        items={tabItems}
      />

      <RouteEventsModal
        visible={isEventsModalVisible}
        routeId={r.id}
        onClose={() => setIsEventsModalVisible(false)}
        title={`История событий маршрута #${r.number}`}
      />

      <Modal
        title="Причина отката маршрута"
        open={reopenModalVisible}
        onCancel={() => setReopenModalVisible(false)}
        onOk={handleReopen}
        okText="Откатить"
        confirmLoading={reopenLoading}
      >
        <Input.TextArea
          rows={4}
          value={reopenReason}
          onChange={(e) => setReopenReason(e.target.value)}
          placeholder="Опишите причину отката (не менее 5 символов)"
        />
      </Modal>

      <Modal
        title={stopAction.type === 'load' ? 'Дата фактической погрузки' : 'Дата фактической выгрузки'}
        open={stopAction.visible}
        onCancel={() => setStopAction({ visible: false, type: null })}
        okText="Подтвердить"
        onOk={async () => {
          if (!stopAction.stopId) return;
          if (!stopAction.date) {
            message.error('Выберите дату');
            return;
          }
          try {
            const payload = {
              routeId: r.id,
              stopId: stopAction.stopId,
              actualAt: stopAction.date.toISOString()
            };
            if (stopAction.type === 'load') {
              await loadStop(payload).unwrap();
              message.success('Погрузка выполнена');
            } else if (stopAction.type === 'unload') {
              await unloadStop(payload).unwrap();
              message.success('Выгрузка выполнена');
            }
            setStopAction({ visible: false, type: null });
            refreshCard();
          } catch (e: unknown) {
            const err = e as { data?: { error?: string } };
            message.error(err?.data?.error || 'Ошибка выполнения операции');
          }
        }}
      >
        <DatePicker
          style={{ width: '100%' }}
          format="DD.MM.YYYY"
          value={stopAction.date}
          onChange={(d) => setStopAction((prev) => ({ ...prev, date: d || undefined }))}
        />
      </Modal>

      {injectionEnabled && view ? (
        <LiveApplicationInjectionWizard
          routeId={routeId}
          view={view}
          open={injectWizardOpen}
          onClose={() => setInjectWizardOpen(false)}
          onSuccess={refreshCard}
        />
      ) : null}
    </PageLayout>
  );
};

export default RouteCardPage;
